import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

type ModalProps = {
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  width?: number;
};

/** Mirrors upstream's `Modal`: dimmed overlay, centred card, Escape to close. */
export function Modal({
  title,
  children,
  footer,
  onClose,
  width = 400,
}: ModalProps) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[600] flex items-center justify-center bg-overlay-background p-4"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="flex max-h-full flex-col overflow-hidden rounded-md border border-modal-border bg-modal-background shadow-large"
        style={{ width }}
      >
        <div className="flex items-center justify-between border-b border-table-border px-4 py-3">
          <h2 className="text-md font-semibold text-page-text">{title}</h2>
        </div>
        <div className="scrollable overflow-auto px-4 py-3">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-table-border px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function FormField({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="mb-3 flex flex-col gap-1">
      <span className="text-sm font-medium text-form-label-text">{label}</span>
      {children}
      {hint && <span className="text-xs text-table-text-light">{hint}</span>}
    </label>
  );
}
